import type { SaveButtonProps } from "components/SaveButton";

import Download from "@mui/icons-material/Download";
import Visibility from "@mui/icons-material/Visibility";
import {
  Box,
  Breakpoint,
  ButtonProps,
  CircularProgress,
  DialogContentProps,
  DialogProps,
  Grid,
  GridProps,
  GridSize,
  IconButton,
  Skeleton,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { type SpecialZoomLevel } from "@react-pdf-viewer/core";
import { Dialog } from "components/Dialog";
import { ReactNode, useMemo, useState } from "react";

import type { DocumentPreviewHighlight } from "./DocumentPreview";

/** One shared empty array, so "nothing to outline" is the same value every render. */
const EMPTY_HIGHLIGHTS: readonly DocumentPreviewHighlight[] = [];

import { FilePreview } from "./FilePreview";
import { FullScreenDocumentPreview } from "./FullScreenDocumentPreview";

type DocumentDialogContainerProps = Omit<DialogProps, "children" | "content"> & {
  /** Function called when the dialog is closed. Can be a simple callback or receive the close reason. */
  onClose:
    | (() => void)
    | ((
        event: React.MouseEvent<HTMLElement>,
        reason: "backdropClick" | "escapeKeyDown" | "cancelButtonClick" | "closeButtonClick"
      ) => void);
  /** Title displayed at the top of the dialog */
  title?: React.ReactNode;
  /** Optional subheader displayed below the title */
  subheader?: string;
  /** Optional description displayed in the content area of the dialog */
  description?: React.ReactNode;
  /** Optional element to display in the title bar to the left of the close button */
  titleBarActionElement?: React.ReactNode;
  /** Additional Primary Actions. These will display to the left of Cancel and Primary action button */
  additionalPrimaryActionElements?: React.ReactNode[];
  /** Secondary Actions will display on the bottom left of the action toolbar */
  secondaryActionElements?: React.ReactNode[];
  cancelButtonProps?: ButtonProps & { text?: string };
  /** Whether to show a primary action (e.g., Save button). Defaults to true. */
  noPrimaryAction?: boolean;
  /** Optionally provide a component to use as the primary action button. `primaryActionButtonProps` do not apply to this */
  primaryActionButton?: React.ReactNode;
  /** Props to configure the Save button (shown if `noPrimaryAction` is false) */
  primaryActionButtonProps?: SaveButtonProps;
  /** Optional styles to apply to the content area of the dialog */
  contentSx?: SxProps;
  /** Props to configure the form container (left column) */
  formContainerProps?: GridProps;
  /** Props to configure the DialogContent area of the dialog */
  DialogContentProps?: DialogContentProps;
  /** Content rendered between the dialog title and scrollable content (e.g., tabs). Does not scroll. */
  stickyHeaderContent?: ReactNode;
  /** Form content to display in the left column */
  children: ReactNode;
  /** URL of the document to preview */
  previewUrl?: string;
  /** File extension of the document (e.g. "pdf", "xlsx", "docx") */
  fileExtension?: string;
  /**
   * Handler to download the source document. When provided (and a preview
   * exists), a download button is shown next to the full-screen preview
   * control. The parent owns the download mechanics (fresh signed URL, filename).
   */
  onDownload?: () => void;
  /** Loading state for an in-flight download — disables the button and shows a spinner. */
  downloadLoading?: boolean;
  /** Loading state for the document preview */
  previewLoading?: boolean;
  /** Whether to show the preview box when preview fails or is unavailable */
  showPreviewBoxIfFailed?: boolean;
  /** Custom sx for the form container (left column) */
  formContainerSx?: SxProps<Theme>;
  /** Custom sx for the preview container (right column) */
  previewContainerSx?: SxProps<Theme>;
  /** Custom sx for the preview box itself */
  previewBoxSx?: SxProps<Theme>;
  /** Whether to hide the cancel button */
  hideCancelButton?: boolean;
  /**
   * Whether to hide the title-bar close button. `onClose` is required here, so
   * a caller that must not be closed mid-action cannot drop the handler to hide
   * the X the way a plain `Dialog` caller can.
   */
  hideCloseButton?: boolean;
  /** Function that returns the form column size based on whether preview is shown */
  formColumnSize?: Partial<Record<Breakpoint, GridSize>>;
  /** Function that returns the preview column size based on whether preview is shown */
  previewColumnSize?: Partial<Record<Breakpoint, GridSize>>;
  /**
   * Spacing (theme units) between the form and preview columns, and between the
   * two when a narrow window stacks them. Defaults to 3.
   */
  contentColumnSpacing?: number;
  /** Initial zoom applied to a PDF preview once it loads (e.g. `SpecialZoomLevel.PageFit`). */
  previewDefaultScale?: number | SpecialZoomLevel;
  /**
   * Scroll a region of the PDF into view and outline it — for a dialog whose form
   * can say where on the page the value being edited came from.
   */
  previewHighlight?: DocumentPreviewHighlight | null;
  /**
   * Hide the in-column "Preview" header (label + full-screen eye) so the preview
   * pane fills from the top, aligned with the form column. Callers that set this
   * are expected to surface their own preview/full-screen control (e.g. in the
   * title bar).
   */
  hidePreviewHeader?: boolean;
};

export function DocumentDialogContainer({
  children,
  previewUrl,
  fileExtension,
  onDownload,
  downloadLoading = false,
  previewLoading = false,
  showPreviewBoxIfFailed = true,
  formContainerSx,
  previewContainerSx,
  previewBoxSx,
  onClose,
  title,
  subheader,
  description,
  titleBarActionElement,
  additionalPrimaryActionElements,
  secondaryActionElements,
  cancelButtonProps,
  noPrimaryAction,
  primaryActionButton,
  primaryActionButtonProps,
  contentSx,
  DialogContentProps,
  stickyHeaderContent,
  formContainerProps,
  hideCancelButton,
  formColumnSize,
  previewColumnSize,
  contentColumnSpacing = 3,
  previewDefaultScale,
  previewHighlight,
  hidePreviewHeader = false,
  ...dialogProps
}: DocumentDialogContainerProps) {
  const theme = useTheme();
  const [fullScreenPreviewOpen, setFullScreenPreviewOpen] = useState(false);

  const showPreview = !!(previewUrl || showPreviewBoxIfFailed);
  // Held stable so the memoized preview can skip a render. Rebuilt inline it
  // was a new array on every keystroke, which defeated the memo and re-rendered
  // the PDF viewer for a change it had no stake in.
  const previewHighlights = useMemo(
    () => (previewHighlight == null ? EMPTY_HIGHLIGHTS : [previewHighlight]),
    [previewHighlight]
  );

  // Default column sizes (old implementation)
  const defaultFormSize = showPreview ? { xs: 12, md: 6 } : { xs: 12, md: 12 };
  const defaultPreviewSize = { xs: 12, md: 6 };

  const formSize = formColumnSize ?? defaultFormSize;
  const previewSize = previewColumnSize ?? defaultPreviewSize;

  return (
    <Dialog
      {...dialogProps}
      hideCancelButton={hideCancelButton}
      onClose={onClose}
      title={title}
      subheader={subheader}
      description={description}
      titleBarActionElement={titleBarActionElement}
      additionalPrimaryActionElements={additionalPrimaryActionElements}
      secondaryActionElements={secondaryActionElements}
      cancelButtonProps={cancelButtonProps}
      noPrimaryAction={noPrimaryAction}
      primaryActionButton={primaryActionButton}
      primaryActionButtonProps={primaryActionButtonProps}
      stickyHeaderContent={stickyHeaderContent}
      contentSx={contentSx}
      DialogContentProps={{
        ...DialogContentProps,
        sx: {
          overflowY: "auto",
          ...DialogContentProps?.sx,
        },
      }}
    >
      <Grid
        container
        columnSpacing={contentColumnSpacing}
        // Also the gap between the two once a narrow window stacks them.
        rowSpacing={contentColumnSpacing}
        alignItems="stretch"
        sx={{ flex: 1, minHeight: 0 }}
      >
        {/* Left Column - Form */}
        <Grid
          container
          size={formSize}
          rowSpacing={1.5}
          columnSpacing={3}
          sx={{
            pr: showPreview ? theme.spacing(4) : 0,
            pt: theme.spacing(1), // or the header gets cut off
            overflowY: "auto",
            ...formContainerSx,
          }}
          {...formContainerProps}
        >
          {children}
        </Grid>

        {/* Right Column - Document Preview */}
        {showPreview && (
          <Grid
            size={previewSize}
            // No `height: 100%` — the row already stretches this column to the
            // form's height, and once a narrow window wraps the two onto
            // separate lines 100% claims the whole container and leaves the form
            // a strip one field tall.
            sx={{
              display: "flex",
              flexDirection: "column",
              ...previewContainerSx,
            }}
          >
            {!hidePreviewHeader && (
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Preview
                </Typography>
                {/* What the outline on the page is pointing at, said in words.
                    The outline alone asks the reviewer to work out which of sixty
                    inputs it belongs to — the complaint that retired the jump
                    control. Named here rather than only on the outline so it is
                    legible while their eyes are on the document. */}
                {previewHighlight?.label != null && (
                  <Typography
                    variant="subtitle2"
                    sx={{ ml: "auto", color: "primary.main", fontWeight: 600 }}
                  >
                    {previewHighlight.label}
                  </Typography>
                )}
                {previewUrl && (
                  <Tooltip title="Open full-screen preview">
                    <IconButton
                      size="small"
                      onClick={() => setFullScreenPreviewOpen(true)}
                      sx={{ ml: 0.5 }}
                      aria-label="Open full-screen preview"
                    >
                      <Visibility sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {previewUrl && onDownload && (
                  <Tooltip title="Download document">
                    {/* span wrapper lets the tooltip work while the button is
                        disabled mid-download */}
                    <span>
                      <IconButton
                        size="small"
                        onClick={onDownload}
                        disabled={downloadLoading}
                        aria-label="Download document"
                      >
                        {downloadLoading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Download sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            )}
            {previewLoading ? (
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  flex: 1,
                  minHeight: 0,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.spacing(0.5),
                  p: 2,
                  ...previewBoxSx,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  sx={{
                    flex: 1,
                    borderRadius: theme.spacing(0.5),
                  }}
                />
              </Box>
            ) : previewUrl ? (
              <Box
                sx={{
                  width: "100%",
                  flex: 1,
                  minHeight: 0,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ position: "absolute", inset: 0 }}>
                  <FilePreview
                    fileUrl={previewUrl}
                    fileExtension={fileExtension}
                    defaultScale={previewDefaultScale}
                    // One region, drawn and framed: on a review surface the field
                    // the reviewer is in is both what to outline and what to bring
                    // into view.
                    highlights={previewHighlights}
                    frame={previewHighlight}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.spacing(0.5),
                  ...previewBoxSx,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No preview available
                </Typography>
              </Box>
            )}
            {previewUrl && (
              <FullScreenDocumentPreview
                open={fullScreenPreviewOpen}
                onClose={() => setFullScreenPreviewOpen(false)}
                fileUrl={previewUrl}
                fileExtension={fileExtension}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Dialog>
  );
}
